import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Brain, ChartLine, Lock, ChatCircle } from "@phosphor-icons/react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { OG_IMAGES, SITE_URL} from "@/lib/seo";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/5wim757c_GB3lwDYDszQwHRIL.png";

const WINGMEN = [
  {
    id: "jarvis",
    name: "Jarvis",
    tagline: "The Wingman",
    role: "Lead Operations Orchestrator",
    specialty: "The Central Nervous System",
    icon: ChatCircle,
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/cnc94akb_MBkT-V-JVMAw9m3u.png",
    body: [
      "Jarvis doesn't just chat — he executes. He manages the HIPAA-compliant flow through Keragon, syncing data between Healthie, Notion, and the clinical team in real time.",
      "His mandate is operational integrity: ensure no patient ever falls through the cracks, no signal is lost, no follow-up is missed.",
    ],
  },
  {
    id: "einstein",
    name: "Einstein",
    tagline: "The Clinical Architect",
    role: "Chief Logic & CPI Analyzer",
    specialty: "The Brain of the IWCE",
    icon: Brain,
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/fstczm0c_GB7Lf3i4n-0bnefk.png",
    body: [
      "Einstein performs the deep-tier 10-domain CPI scoring (/100), mapping biomarkers to functional optimal ranges — not just conventional 'normals.'",
      "From those scores, he derives a patient's unique clinical archetype — the foundation for every downstream protocol decision.",
    ],
  },
  {
    id: "atlas",
    name: "Atlas",
    tagline: "The Longitudinal Strategist",
    role: "Head of Evidence & Digital Twins",
    specialty: "Guardian of the Timeline",
    icon: ChartLine,
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/mhw7wyng_AQSdG8D-jlGInU8E.png",
    body: [
      "Atlas builds and maintains the Digital Twin for every patient, tracking biomarker velocity over time and across domains.",
      "He cross-references every change against the Integrated Evidence Cache — ensuring every protocol is grounded in the latest RCTs and clinical data.",
    ],
  },
  {
    id: "socrates",
    name: "Socrates",
    tagline: "The Protocol Auditor",
    role: "Safety & Integrity Director",
    specialty: "The Ethical Core",
    icon: ShieldCheck,
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/iz8o751i_hfUU2M7ExjWjHidu.png",
    body: [
      "Socrates performs the mandatory contraindication scans on every tiered protocol, against every existing medication, supplement, and condition in the patient record.",
      "His mandate is absolute: the pursuit of optimization never compromises safety.",
    ],
  },
];

function WingmanCard({ w, index }) {
  const Icon = w.icon;
  const reversed = index % 2 === 1;
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65 }}
      data-testid={`wingman-${w.id}`}
      className="rounded-3xl overflow-hidden border-2 border-primary/30 bg-foreground text-background shadow-2xl"
    >
      <div className={`grid grid-cols-1 md:grid-cols-12 items-stretch ${reversed ? "md:[&>*:first-child]:order-2" : ""}`}>
        {/* Image */}
        <div className="md:col-span-6 relative aspect-video md:aspect-auto bg-foreground overflow-hidden">
          <img
            src={w.image}
            alt={`${w.name} — ${w.role} · ${w.specialty}`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-foreground/30 via-transparent to-transparent" />
          <div className="absolute top-4 left-4">
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground/85 backdrop-blur-sm border border-accent/40"
              style={{ color: "hsl(38 38% 95%)" }}
            >
              <Icon size={14} weight="duotone" className="text-accent" />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.22em]">
                {w.tagline}
              </span>
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="md:col-span-6 p-8 md:p-10 flex flex-col justify-center">
          <div
            className="font-mono text-[0.6rem] uppercase tracking-[0.22em] mb-3"
            style={{ color: "hsl(220 18% 72%)" }}
          >
            {w.role}
          </div>
          <h3
            className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.0]"
            style={{ color: "hsl(38 38% 95%)" }}
          >
            {w.name}
          </h3>
          <p className="mt-3 font-display text-lg md:text-xl text-accent italic">
            {w.specialty}
          </p>
          <div className="mt-6 space-y-3">
            {w.body.map((p) => (
              <p
                key={p.slice(0, 30)}
                className="text-base md:text-lg text-background/80 leading-relaxed"
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function AIAgents() {
  useDocumentMeta({
    title: "Talk to Rhea — Integrated Optimization Agent | Integrated Clinical Intelligence",
    description:
      "Talk to Rhea, our Integrated Optimization Agent. A guided conversation helps organize your goals and identify the right Integrated Wellness next step — no signup required.",
    image: OG_IMAGES.aiAgents,
    url: `${SITE_URL}/ai-agents`,
  });

  return (
    <div data-testid="ai-agents-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <main className="pt-32 md:pt-36 pb-20 md:pb-28">
        {/* Top */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <Link
            to="/"
            data-testid="ai-agents-back-link"
            className="eyebrow inline-flex items-center gap-2 text-foreground/65 hover:text-primary transition-colors"
          >
            <span aria-hidden>←</span> Back to Integrated Clinical Intelligence
          </Link>
          <div className="mt-8 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/35 bg-card">
            <Lock size={14} weight="duotone" className="text-primary" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.32em] text-primary">
              Zero-Trust Clinical Pipeline
            </span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-6 font-display font-bold tracking-[-0.03em] leading-[0.98]
                       text-[clamp(2.5rem,6vw,5rem)]"
          >
            The Integrated Matrix.<br />
            <em className="not-italic text-primary">A new class of clinical intelligence.</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-7 text-lg md:text-xl text-foreground/80 leading-relaxed max-w-3xl mx-auto"
          >
            This is not an off-the-shelf AI solution. The Integrated Matrix is a Zero-Trust
            Clinical Pipeline engineered personally by{" "}
            <strong className="text-foreground">John Campetella</strong> — a{" "}
            <em className="not-italic text-primary">Triple-Brain</em> architecture
            (Reasoning · Research · Engineering) designed to automate the most complex layers
            of functional medicine with total precision.
          </motion.p>
        </section>

        {/* Hero architecture image */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-14 md:mt-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9 }}
            className="relative aspect-[16/9] rounded-3xl overflow-hidden border-2 border-primary/25 gold-glow bg-foreground"
          >
            <img
              src={HERO_IMG}
              alt="The Integrated Matrix — clinical intelligence pipeline orchestrating Keragon, Healthie EHR, and the CPI Engine"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent" />
          </motion.div>
        </section>

        {/* The Ambassadors — Front Gate */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 mt-24 md:mt-28">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="font-mono text-[0.65rem] uppercase tracking-[0.32em] text-primary/80 mb-4">
              — The Front Gate
            </div>
            <h2 className="font-display font-semibold tracking-[-0.02em] leading-[1.05] text-[clamp(2rem,4.5vw,3.25rem)]">
              The <em className="not-italic text-primary">Ambassadors</em>.
            </h2>
            <p className="mt-3 font-display text-xl md:text-2xl text-foreground/65 italic">
              Jotform AI Agents
            </p>
          </motion.div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 text-base md:text-lg text-foreground/80 leading-relaxed">
            <div className="p-6 rounded-2xl border border-border bg-card">
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/75 mb-2">
                Identity
              </div>
              <p>High-Availability Patient Coordinators.</p>
            </div>
            <div className="p-6 rounded-2xl border border-border bg-card">
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/75 mb-2">
                Tech
              </div>
              <p>
                Built on the Jotform Enterprise framework — and programmed by John{" "}
                <em className="not-italic text-primary">beyond</em> the limits of standard
                Jotform technology.
              </p>
            </div>
            <div className="p-6 rounded-2xl border border-border bg-card">
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/75 mb-2">
                Role
              </div>
              <p>
                The first point of contact — handling triage and intake with a level of
                clinical context that standard chatbots can't touch.
              </p>
            </div>
          </div>
        </section>

        {/* The Wingman Intelligence Fleet */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-24 md:mt-28">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="font-mono text-[0.65rem] uppercase tracking-[0.32em] text-primary/80 mb-4">
              — The Deep Engine
            </div>
            <h2 className="font-display font-semibold tracking-[-0.02em] leading-[1.05] text-[clamp(2rem,4.5vw,3.25rem)]">
              The <em className="not-italic text-primary">Wingman Intelligence Fleet</em>.
            </h2>
            <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
              While the Ambassadors greet you, the Wingmen — led by Jarvis — execute the science.
            </p>
          </motion.div>

          <div className="mt-14 space-y-8 md:space-y-10">
            {WINGMEN.map((w, i) => (
              <WingmanCard key={w.id} w={w} index={i} />
            ))}
          </div>
        </section>

        {/* Closing */}
        <section className="max-w-3xl mx-auto px-4 md:px-8 mt-24 md:mt-28 text-center">
          <p className="font-display text-2xl md:text-3xl font-semibold leading-snug text-foreground/85">
            One Pipeline. Four Wingmen. Zero compromise on{" "}
            <em className="not-italic text-primary">safety</em>,{" "}
            <em className="not-italic text-primary">evidence</em>, or{" "}
            <em className="not-italic text-primary">precision</em>.
          </p>
          <Link
            to="/contact"
            data-testid="ai-agents-contact-cta"
            className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-full
                       bg-primary text-primary-foreground hover:bg-primary/90
                       font-semibold text-sm md:text-base
                       hover:gap-3 transition-all shadow-lg"
          >
            Start My Health Assessment <span aria-hidden className="text-base">→</span>
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
